import SurveyItem, { SurveyItemTypes } from './SurveyItem';
import Survey from './Survey';
import { defineMessages, MessageDescriptor } from 'react-intl';
import PrimarySurveyItemIdentifier from './PrimarySurveyItemdentifier';

export type AggregationType = 'stats' | 'group' | 'free-text' | 'date';

export const statsType: SurveyItemTypes[] = ['decimal', 'integer', 'quantity'];

const messages = defineMessages({
  true: {
    id: 'responseSearchItem.true',
    defaultMessage: 'True',
  },
  false: {
    id: 'responseSearchItem.false',
    defaultMessage: 'False',
  },
});

/**
 * A class that wraps a survey item and provides utilites for results for survey
 * questions using the fhir search service
 */
export default class FhirSearchItem {
  private _surveyItem: SurveyItem;
  private _surveyId: string;

  static initFromSurvey(survey: Survey, linkId: string) {
    const targetItem = survey.getSurveyItemWithLinkId(linkId);
    return new FhirSearchItem(targetItem, survey.id);
  }

  constructor(surveyItem: SurveyItem, surveyId: string) {
    // todo: make survey ID required when refactor complete
    this._surveyItem = surveyItem;
    this._surveyId = surveyId;
  }

  get primarySurveyItemIdentifer(): PrimarySurveyItemIdentifier {
    return new PrimarySurveyItemIdentifier(
      this.sourceSurveyId,
      this.sourceSurveyItemLinkId,
    );
  }

  get sourceSurveyId(): string {
    return this._surveyId;
  }

  get sourceSurveyItemLinkId(): string {
    return this._surveyItem.link;
  }

  get code(): string {
    return this._surveyItem.code;
  }

  get title(): string {
    return this._surveyItem.text;
  }

  get surveyItem(): SurveyItem {
    return this._surveyItem;
  }

  get aggregationType(): AggregationType {
    if (this._surveyItem.type === 'string') {
      return 'free-text';
    } else if (statsType.includes(this._surveyItem.type)) {
      return 'stats';
    } else if (this._surveyItem.responseValueType === 'valueDate') {
      return 'date';
    } else {
      return 'group';
    }
  }

  get key(): string {
    return this._surveyItem.link;
  }

  get queryValueKey(): string {
    let key: string = this._surveyItem.responseValueType;

    if (['valueQuantity', 'valueInteger', 'valueDecimal'].includes(key)) {
      return `valueQuantity.value`;
    } else if (key === 'valueDate') {
      key = 'valueDateTime';
    } else if (key === 'valueUri') {
      key = 'valueString';
    } else if (key === 'valueCoding') {
      key = 'valueCodeableConcept.coding.code';
    } else if (key === 'valueBoolean') {
      return key;
    }

    return `${key}.keyword`;
  }

  mapKey(
    key: string | number,
    formatMessage: (message: MessageDescriptor) => string,
  ): string | number {
    // elasticsearch returns booleans as 1 or 0, make it more human readable
    if (this._surveyItem.responseValueType === 'valueBoolean') {
      return formatMessage(key === 0 ? messages.false : messages.true);
    } else {
      return key;
    }
  }
}
